import { HttpClient, HttpErrorResponse, HttpEventType, provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { createEnvironmentInjector, EnvironmentInjector, inject, Service } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'environments/environment';
import { fromEvent, merge, Observable, take, throwError, timeout, TimeoutError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { IData } from '../../models/api/data';
import { IError } from '../../models/error/error';
import { IGeneratedSignedUrlMedia, IGetFreeSpace, IUploadMedia } from '../../models/media/media';
import { httpInterceptor } from 'app/core/helpers/https.interceptor';

const urlGenerateSignedUrlMedia = () => ['clinic', 'admin', 'media', 'generate-signed-url'];
const urlGetFreeSpace = (laboratoryId: string) => ['clinic', 'admin', 'media', 'laboratory', laboratoryId, 'free-space'];
export const UPLOAD_CONNECTION_LOST = 'UPLOAD_CONNECTION_LOST';

@Service()
export class MediaService {
    private readonly router = inject(Router);
    private readonly httpClient = inject(HttpClient);

    private readonly xhrHttpClient = createEnvironmentInjector(
        [provideHttpClient(withXhr(), withInterceptors([httpInterceptor]))],
        inject(EnvironmentInjector),
    ).get(HttpClient);

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);
    constructor() {}

    public uploadMedia(data: IUploadMedia): Observable<IData<IGeneratedSignedUrlMedia>> {
        const { file, ...queryParams } = data;

        const url = this.router.createUrlTree(urlGenerateSignedUrlMedia(), {
            queryParams,
        });

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.post<IData<IGeneratedSignedUrlMedia>>(parsedUrl, { originalName: file.name }, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
            switchMap((resDataSinedUrl: IData<IGeneratedSignedUrlMedia>) => {
                return this.httpClient
                    .put(resDataSinedUrl.data.signedUrl, file, {
                        headers: {
                            'Content-Type': resDataSinedUrl.data.contentType,
                        },
                    })
                    .pipe(
                        map(() => {
                            return resDataSinedUrl;
                        }),
                        catchError((error: HttpErrorResponse) => {
                            if (error.error instanceof ErrorEvent) {
                                return throwError(() => `Error: ${error.error.message}`);
                            }
                            const errorApi: IError = error.error.error;
                            return throwError(() => errorApi);
                        }),
                    );
            }),
        );
    }

    public uploadMediaWithProgress(file: File): Observable<IData<IGeneratedSignedUrlMedia> | number> {
        const url = this.router.createUrlTree(urlGenerateSignedUrlMedia());

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.xhrHttpClient
            .post<IData<IGeneratedSignedUrlMedia> | number>(parsedUrl, { originalName: file.name }, { responseType: 'json' })
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    if (error.error instanceof ErrorEvent) {
                        return throwError(() => `Error: ${error.error.message}`);
                    }
                    const errorApi: IError = error.error.error;
                    return throwError(() => errorApi);
                }),
                switchMap((resDataSignedUrl: IData<IGeneratedSignedUrlMedia>) => {
                    const offlineError$: Observable<never> = fromEvent(window, 'offline').pipe(
                        take(1),
                        switchMap(() => throwError(() => UPLOAD_CONNECTION_LOST)),
                    );

                    const upload$ = this.xhrHttpClient
                        .put(resDataSignedUrl.data.signedUrl, file, {
                            reportUploadProgress: true,
                            observe: 'events',
                        })
                        .pipe(
                            timeout({ each: 30_000 }),
                            map((event) => {
                                if (event.type === HttpEventType.Response) {
                                    return resDataSignedUrl;
                                }

                                if (event.type === HttpEventType.DownloadProgress || event.type === HttpEventType.UploadProgress) {
                                    return Math.round((event.loaded / event.total) * 100);
                                }

                                if (event.type === HttpEventType.ResponseHeader) {
                                    return 100;
                                }

                                return 0;
                            }),
                            catchError((error) => {
                                if (error instanceof TimeoutError) {
                                    return throwError(() => UPLOAD_CONNECTION_LOST);
                                }
                                if (error.error instanceof ErrorEvent) {
                                    return throwError(() => `Error: ${error.error.message}`);
                                }
                                const errorApi: IError = error.error.error;
                                return throwError(() => errorApi);
                            }),
                        );

                    return merge(upload$, offlineError$);
                }),
            );
    }

    public getFreeSpace(data: IGetFreeSpace): Observable<IData<{ freeSpace: number }>> {
        const { laboratoryId, ...params } = data;
        const url = this.router.createUrlTree(urlGetFreeSpace(laboratoryId), {
            queryParams: params,
        });

        const parsedUrl = `${environment.apiUrl}/${environment.apiVersion}${url}`;

        return this.httpClient.get<IData<{ freeSpace: number }>>(parsedUrl, { responseType: 'json' }).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.error instanceof ErrorEvent) {
                    return throwError(() => `Error: ${error.error.message}`);
                }
                const errorApi: IError = error.error.error;
                return throwError(() => errorApi);
            }),
        );
    }
}
